import React, { useEffect, useRef, useState } from "react";
import { getAnalyser } from "@/lib/audioGraph";

/**
 * R.128-styled stereo audio meter.
 *
 *  - When `active` is true AND a persistent AnalyserNode exists on the given
 *    <video> element (see /app/frontend/src/lib/audioGraph.js), the meter
 *    reads real-time PCM samples and displays true RMS + peak. Label reads
 *    "LIVE".
 *  - Otherwise the meter animates smoothly from the `lufs` prop (encoder-
 *    authored loudness pushed over the WebSocket tick). Label reads "LUFS".
 *
 * The meter never creates or tears down Web Audio nodes — that lifecycle is
 * owned by the Dashboard via the audioGraph module, so the meter can never
 * accidentally break the audio pipeline.
 */

function dbToNorm(db) {
  if (!Number.isFinite(db)) return 0;
  const clamped = Math.max(-60, Math.min(0, db));
  return (clamped + 60) / 60;
}

function linToDb(x) {
  if (x <= 0.0001) return -80;
  return 20 * Math.log10(x);
}

function Bar({ level, peak, targetLufs = -23 }) {
  const target = dbToNorm(targetLufs);
  const upper = dbToNorm(-18);
  return (
    <div className="relative w-1.5 h-16 bg-zinc-950 border border-zinc-800 rounded-[1px] overflow-hidden">
      <div
        className={`absolute inset-x-0 h-px ${targetLufs === -24 ? "bg-amber-400" : "bg-emerald-500/60"}`}
        style={{ bottom: `${target * 100}%` }}
      />
      <div className="absolute inset-x-0 h-px bg-amber-500/60" style={{ bottom: `${upper * 100}%` }} />
      <div
        className="absolute inset-x-0 bottom-0"
        style={{
          height: `${level * 100}%`,
          background:
            "linear-gradient(to top, #10B981 0%, #10B981 65%, #F59E0B 65%, #F59E0B 85%, #EF4444 85%, #EF4444 100%)",
        }}
      />
      {peak > 0.01 && (
        <div
          className="absolute inset-x-0 h-0.5 bg-white/90 shadow-[0_0_2px_rgba(255,255,255,0.8)]"
          style={{ bottom: `calc(${peak * 100}% - 1px)` }}
        />
      )}
    </div>
  );
}

function Sparkline({ history }) {
  if (history.length < 2) {
    return <div className="w-14 h-4 border border-zinc-800 bg-zinc-950 rounded-[1px]" />;
  }
  const W = 56;
  const H = 16;
  const min = -60;
  const max = 0;
  const step = W / Math.max(1, history.length - 1);
  const points = history
    .map((v, i) => {
      const y = H - ((Math.max(min, Math.min(max, v)) - min) / (max - min)) * H;
      return `${(i * step).toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const last = history[history.length - 1];
  const lastNorm = (Math.max(min, Math.min(max, last)) - min) / (max - min);
  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      className="border border-zinc-800 bg-zinc-950 rounded-[1px]"
    >
      <line
        x1="0"
        y1={H - dbToNorm(-23) * H}
        x2={W}
        y2={H - dbToNorm(-23) * H}
        stroke="#10B981"
        strokeOpacity="0.35"
        strokeDasharray="2 2"
      />
      <polyline points={points} fill="none" stroke="#10B981" strokeWidth="1.2" />
      <circle cx={W - 1} cy={H - lastNorm * H} r="1.5" fill="#10B981" />
    </svg>
  );
}

export default function AudioMeter({
  lufs = -60,
  active = false,
  videoEl = null,
  ducking = false,
  truePeakDbtp = null,
  testId,
  loudnessTarget = -24, // LKFS (ATSC A/85). Override to -23 for EBU R.128.
  loudnessSpec = "ATSC A/85",
  channelLayout = "5.1", // "5.1", "2.0", "7.1"
  showLayout = false,
}) {
  const [levels, setLevels] = useState({ l: 0, r: 0, peakL: 0, peakR: 0 });
  const [mode, setMode] = useState("sim");
  const [readout, setReadout] = useState(lufs);
  const [tpLatched, setTpLatched] = useState(false);
  const [history, setHistory] = useState([]);
  const rafRef = useRef();
  const lastHistoryPushRef = useRef(0);

  // Ducking dips the reference target ~6 LU down from operating anchor,
  // otherwise reference sits at operating anchor (default -18 dB anchor line)
  const targetLufs = ducking ? loudnessTarget : -18;

  // Latching true-peak > 0 dBTP warning (auto clears after 4 s)
  useEffect(() => {
    if (truePeakDbtp != null && truePeakDbtp > 0) {
      setTpLatched(true);
      const t = setTimeout(() => setTpLatched(false), 4000);
      return () => clearTimeout(t);
    }
  }, [truePeakDbtp]);

  useEffect(() => {
    let phase = Math.random() * 100;
    let peakL = 0;
    let peakR = 0;
    let lastPeakL = 0;
    let lastPeakR = 0;

    // Reuse a single analyser buffer per effect run.
    const analyser = active && videoEl ? getAnalyser(videoEl) : null;
    const buf = analyser ? new Float32Array(analyser.fftSize) : null;
    if (analyser) setMode("live");
    else setMode("sim");

    const tick = () => {
      let lNorm, rNorm, displayDb;

      if (analyser && buf) {
        analyser.getFloatTimeDomainData(buf);
        let sum = 0;
        let peak = 0;
        for (let i = 0; i < buf.length; i++) {
          const a = Math.abs(buf[i]);
          if (a > peak) peak = a;
          sum += buf[i] * buf[i];
        }
        const rms = Math.sqrt(sum / buf.length);
        const rmsDb = linToDb(rms);
        const peakDb = linToDb(peak);
        lNorm = dbToNorm(rmsDb);
        rNorm = dbToNorm(rmsDb * 0.97);
        displayDb = peakDb;
        const peakN = dbToNorm(peakDb);
        if (peakN > peakL) peakL = peakN;
        if (peakN > peakR) peakR = peakN;
      } else {
        phase += 0.14;
        const base = dbToNorm(lufs);
        const jL = 0.08 * Math.sin(phase * 1.15) + 0.05 * Math.sin(phase * 3.6);
        const jR = 0.08 * Math.cos(phase * 1.08) + 0.05 * Math.sin(phase * 3.3);
        lNorm = Math.max(0, base + jL);
        rNorm = Math.max(0, base + jR);
        displayDb = lufs;
        const peakJitL = lNorm + 0.05 + 0.03 * Math.sin(phase * 8);
        const peakJitR = rNorm + 0.05 + 0.03 * Math.cos(phase * 7);
        if (peakJitL > peakL) peakL = Math.min(1, peakJitL);
        if (peakJitR > peakR) peakR = Math.min(1, peakJitR);
      }

      const now = performance.now();
      if (peakL <= lNorm) lastPeakL = now;
      if (peakR <= rNorm) lastPeakR = now;
      if (now - lastPeakL > 600) peakL = Math.max(lNorm, peakL - 0.006);
      if (now - lastPeakR > 600) peakR = Math.max(rNorm, peakR - 0.006);

      setLevels({ l: lNorm, r: rNorm, peakL, peakR });
      setReadout(displayDb);

      // Push a history sample once per second for the sparkline
      const nowPerf = performance.now();
      if (nowPerf - lastHistoryPushRef.current > 1000) {
        lastHistoryPushRef.current = nowPerf;
        setHistory((h) => {
          const next = h.length >= 60 ? h.slice(1) : h.slice();
          next.push(displayDb);
          return next;
        });
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [lufs, active, videoEl]);

  const clip = readout > -1;

  return (
    <div className="flex flex-col items-center gap-1" data-testid={testId}>
      {/* DUCK chip (SCTE-35 driven ad-break loudness dip) */}
      {ducking && (
        <div
          data-testid={testId ? `${testId}-duck` : undefined}
          className="font-mono text-[8px] uppercase tracking-widest px-1 py-px rounded-[1px] bg-amber-950/70 text-amber-300 border border-amber-500/40"
          title="SCTE-35 ad break — reference dipped to target LKFS"
        >
          Duck
        </div>
      )}
      {/* True-peak warning (latching) */}
      {tpLatched && (
        <div
          data-testid={testId ? `${testId}-tp` : undefined}
          className="font-mono text-[8px] uppercase tracking-widest px-1 py-px rounded-[1px] bg-red-950/80 text-red-300 border border-red-500/50"
          title={`True peak ${truePeakDbtp?.toFixed?.(1)} dBTP > 0 — potential clipping`}
        >
          TP
        </div>
      )}
      <div className="flex gap-[2px]">
        <Bar level={levels.l} peak={levels.peakL} targetLufs={targetLufs} />
        <Bar level={levels.r} peak={levels.peakR} targetLufs={targetLufs} />
      </div>
      <div
        className={`font-mono text-[9px] leading-tight px-1 rounded-[1px] ${
          clip
            ? "text-red-400 bg-red-950/70"
            : mode === "live"
            ? "text-emerald-300 bg-black/70"
            : "text-zinc-300 bg-black/70"
        }`}
        title={mode === "live" ? "Real-time Web Audio analysis" : "Encoder loudness (WS)"}
      >
        {(Number.isFinite(readout) ? readout : 0).toFixed(1)}
      </div>
      <div className="font-mono text-[8px] uppercase tracking-widest text-zinc-500" title={`Reference: ${loudnessTarget} LKFS · ${loudnessSpec}`}>
        {mode === "live" ? "LIVE" : loudnessSpec === "EBU R.128" ? "LUFS" : "LKFS"}
      </div>
      <Sparkline history={history} />
      {showLayout && (
        <div
          data-testid={testId ? `${testId}-layout` : undefined}
          className="font-mono text-[8px] uppercase tracking-widest text-zinc-400 border border-zinc-800 bg-zinc-950 rounded-[1px] px-1 leading-tight"
          title={`${channelLayout} primary mix · target ${loudnessTarget} LKFS`}
        >
          {channelLayout}
        </div>
      )}
    </div>
  );
}
